"use client";

import React, { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { useOnboarding, StepPresence } from "./OnboardingContext";
import type { OnboardingStepKey } from "./steps";
import { cn } from "@/lib/utils";

interface PresenceInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Unique field name for presence tracking */
  fieldName: string;
  /** The step this field belongs to */
  stepKey: OnboardingStepKey;
  /** Optional className for the wrapper */
  wrapperClassName?: string;
}

/**
 * An Input component with real-time presence awareness.
 * Shows a colored border and user name when another user is editing this field.
 */
export function PresenceInput({
  fieldName,
  stepKey,
  wrapperClassName,
  className,
  onFocus,
  onBlur,
  ...props
}: PresenceInputProps) {
  const { setActiveField, getFieldPresence, stepData } = useOnboarding();
  const fieldPresence = getFieldPresence(stepKey);
  const otherUsers = fieldPresence[fieldName] || [];
  const hasOtherUsers = otherUsers.length > 0;
  
  // Get the remote value for this field (from other users' edits)
  const remoteData = stepData[stepKey]?.data;
  const remoteValue = remoteData?.[fieldName];

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setActiveField(fieldName);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setActiveField(null);
    onBlur?.(e);
  };

  // Build display text for presence badge
  const presenceText = otherUsers.length === 1
    ? otherUsers[0].userName.split(' ')[0]
    : `${otherUsers.length} users`;
  
  // Build tooltip with all user names
  const presenceTooltip = otherUsers.map(u => u.userName).join(', ');

  return (
    <div className={cn("relative", wrapperClassName)}>
      {/* Presence indicator badge - positioned above the input */}
      {hasOtherUsers && (
        <div className="absolute -top-5 right-0 z-10">
          <span 
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500 text-white text-[10px] font-medium shadow-sm"
            title={presenceTooltip}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
            </span>
            {presenceText}
          </span>
        </div>
      )}
      <Input
        {...props}
        className={cn(
          className,
          hasOtherUsers && "ring-2 ring-green-500/50 border-green-500/50"
        )}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </div>
  );
}

/**
 * Hook to sync local state with remote changes from other users.
 * Returns the remote value and whether it was updated by someone else.
 */
export function useRemoteFieldValue<T>(
  stepKey: OnboardingStepKey,
  fieldName: string,
  localValue: T,
  setLocalValue: (value: T) => void
): { isRemoteUpdate: boolean } {
  const { stepData, getFieldPresence } = useOnboarding();
  const lastLocalValueRef = useRef<T>(localValue);
  const isRemoteUpdateRef = useRef(false);
  
  const remoteData = stepData[stepKey]?.data;
  const remoteValue = remoteData?.[fieldName] as T | undefined;
  const fieldPresence = getFieldPresence(stepKey);
  const otherUsersEditing = (fieldPresence[fieldName] || []).length > 0;

  useEffect(() => {
    // Only sync if:
    // 1. Remote value exists and is different from local
    // 2. We're not the one editing (another user made the change)
    // 3. The local value hasn't changed since last sync (user isn't actively typing)
    if (
      remoteValue !== undefined &&
      remoteValue !== localValue &&
      !otherUsersEditing &&
      lastLocalValueRef.current === localValue
    ) {
      isRemoteUpdateRef.current = true;
      setLocalValue(remoteValue);
      lastLocalValueRef.current = remoteValue;
      
      // Reset flag after a short delay
      setTimeout(() => {
        isRemoteUpdateRef.current = false;
      }, 100);
    } else {
      lastLocalValueRef.current = localValue;
    }
  }, [remoteValue, localValue, setLocalValue, otherUsersEditing]);

  return { isRemoteUpdate: isRemoteUpdateRef.current };
}
